import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';

interface OTAIntegration {
  _id: string;
  name: string;
  status: 'active' | 'inactive' | 'pending' | 'error';
  settings: {
    syncEnabled: boolean;
    autoConfirm: boolean;
    priceMarkup: number;
    minLeadTime: number;
    maxAdvanceBooking: number;
  };
  channelManager: {
    lastSync: string;
    syncFrequency: number;
    inventoryPool: string;
  };
  statistics: {
    totalBookings: number;
    totalRevenue: number;
    averageBookingValue: number;
    cancellationRate: number;
    conversionRate: number;
  };
}

interface OTABooking {
  _id: string;
  otaBookingId: string;
  status: string;
  syncStatus: string;
  guestInfo: {
    firstName: string;
    lastName: string;
    email: string;
  };
  bookingDetails: {
    checkIn: string;
    checkOut: string;
  };
  pricing: {
    otaAmount: number;
  };
}

const OTAManagement: React.FC = () => {
  const [integrations, setIntegrations] = useState<OTAIntegration[]>([]);
  const [bookings, setBookings] = useState<OTABooking[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [setupDialog, setSetupDialog] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [credentials, setCredentials] = useState({
    apiKey: '',
    apiSecret: '',
    propertyId: '',
    accountId: ''
  });
  const [settings, setSettings] = useState({
    syncEnabled: true,
    autoConfirm: false,
    priceMarkup: 0,
    minLeadTime: 1,
    maxAdvanceBooking: 365
  });
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const platforms = [
    { value: 'campspot', label: 'CampSpot', color: '#4CAF50' },
    { value: 'booking.com', label: 'Booking.com', color: '#003580' },
    { value: 'expedia', label: 'Expedia', color: '#FFC72C' },
    { value: 'airbnb', label: 'Airbnb', color: '#FF5A5F' },
    { value: 'vrbo', label: 'VRBO', color: '#1A6CE7' },
    { value: 'hipcamp', label: 'Hipcamp', color: '#2A9D8F' }
  ];

  useEffect(() => {
    fetchIntegrations();
    fetchBookings();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get('/api/ota/integrations');
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get('/api/ota/bookings', {
        params: { page: page + 1, limit: rowsPerPage }
      });
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const handleSetupIntegration = async () => {
    try {
      await axios.post(`/api/ota/integrations/${selectedPlatform}`, {
        credentials,
        settings
      });
      setSetupDialog(false);
      fetchIntegrations();
      setCredentials({ apiKey: '', apiSecret: '', propertyId: '', accountId: '' });
    } catch (error) {
      console.error('Error setting up integration:', error);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await axios.post('/api/ota/sync/all');
      fetchIntegrations();
    } catch (error) {
      console.error('Error syncing:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleIntegration = async (name: string, currentStatus: string) => {
    try {
      if (currentStatus === 'active') {
        await axios.delete(`/api/ota/integrations/${name}`);
      } else {
        // Reactivate
        await axios.post(`/api/ota/integrations/${name}`, {
          credentials: {},
          settings: {}
        });
      }
      fetchIntegrations();
    } catch (error) {
      console.error('Error toggling integration:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <WarningIcon color="warning" />;
      default:
        return <ErrorIcon color="disabled" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const renderOverview = () => {
    const totalBookings = integrations.reduce((sum, i) => sum + i.statistics.totalBookings, 0);
    const totalRevenue = integrations.reduce((sum, i) => sum + i.statistics.totalRevenue, 0);
    const activeIntegrations = integrations.filter(i => i.status === 'active').length;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">OTA Integrations</Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                onClick={handleSyncAll}
                disabled={syncing}
                sx={{ mr: 2 }}
              >
                Sync All
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setSetupDialog(true)}
              >
                Add Integration
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Integrations
                  </Typography>
                  <Typography variant="h4">
                    {activeIntegrations}/{integrations.length}
                  </Typography>
                </Box>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Bookings
                  </Typography>
                  <Typography variant="h4">
                    {totalBookings}
                  </Typography>
                </Box>
                <CalendarIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h4">
                    ${totalRevenue.toFixed(2)}
                  </Typography>
                </Box>
                <AttachMoneyIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg. Booking Value
                  </Typography>
                  <Typography variant="h4">
                    ${totalBookings > 0 ? (totalRevenue / totalBookings).toFixed(2) : '0'}
                  </Typography>
                </Box>
                <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Integration Status</Typography>
            <Grid container spacing={2}>
              {platforms.map((platform) => {
                const integration = integrations.find(i => i.name === platform.value);
                return (
                  <Grid item xs={12} md={4} key={platform.value}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center">
                            {integration && getStatusIcon(integration.status)}
                            <Typography variant="h6" sx={{ ml: 1 }}>
                              {platform.label}
                            </Typography>
                          </Box>
                          {integration ? (
                            <Switch
                              checked={integration.status === 'active'}
                              onChange={() => handleToggleIntegration(platform.value, integration.status)}
                            />
                          ) : (
                            <Button
                              size="small"
                              onClick={() => {
                                setSelectedPlatform(platform.value);
                                setSetupDialog(true);
                              }}
                            >
                              Setup
                            </Button>
                          )}
                        </Box>
                        {integration && (
                          <Box mt={2}>
                            <Typography variant="body2" color="textSecondary">
                              Bookings: {integration.statistics.totalBookings}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Revenue: ${integration.statistics.totalRevenue.toFixed(2)}
                            </Typography>
                            {integration.channelManager.lastSync && (
                              <Typography variant="body2" color="textSecondary">
                                Last sync: {new Date(integration.channelManager.lastSync).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderBookings = () => {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>OTA Bookings</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Booking ID</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Guest</TableCell>
                <TableCell>Check-in</TableCell>
                <TableCell>Check-out</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sync Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map((booking) => (
                <TableRow key={booking._id}>
                  <TableCell>{booking.otaBookingId}</TableCell>
                  <TableCell>
                    <Chip
                      label={(booking as any).otaIntegration?.name || 'Unknown'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {booking.guestInfo.firstName} {booking.guestInfo.lastName}
                  </TableCell>
                  <TableCell>
                    {new Date(booking.bookingDetails.checkIn).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(booking.bookingDetails.checkOut).toLocaleDateString()}
                  </TableCell>
                  <TableCell>${booking.pricing.otaAmount.toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip
                      label={booking.status}
                      size="small"
                      color={getStatusColor(booking.status) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={booking.syncStatus}
                      size="small"
                      color={booking.syncStatus === 'synced' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {booking.status === 'pending' && (
                      <Tooltip title="Confirm Booking">
                        <IconButton
                          size="small"
                          onClick={async () => {
                            await axios.post(`/api/ota/bookings/${booking._id}/confirm`);
                            fetchBookings();
                          }}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={bookings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        />
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs value={selectedTab} onChange={(_, value) => setSelectedTab(value)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Bookings" />
      </Tabs>

      {selectedTab === 0 && renderOverview()}
      {selectedTab === 1 && renderBookings()}

      <Dialog open={setupDialog} onClose={() => setSetupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Setup {platforms.find(p => p.value === selectedPlatform)?.label} Integration
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="API Key"
              value={credentials.apiKey}
              onChange={(e) => setCredentials({ ...credentials, apiKey: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="API Secret"
              type="password"
              value={credentials.apiSecret}
              onChange={(e) => setCredentials({ ...credentials, apiSecret: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Property ID"
              value={credentials.propertyId}
              onChange={(e) => setCredentials({ ...credentials, propertyId: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Account ID (Optional)"
              value={credentials.accountId}
              onChange={(e) => setCredentials({ ...credentials, accountId: e.target.value })}
              margin="normal"
            />
            
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Settings</Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings.syncEnabled}
                  onChange={(e) => setSettings({ ...settings, syncEnabled: e.target.checked })}
                />
              }
              label="Enable Automatic Sync"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoConfirm}
                  onChange={(e) => setSettings({ ...settings, autoConfirm: e.target.checked })}
                />
              }
              label="Auto-confirm Bookings"
            />
            
            <TextField
              fullWidth
              label="Price Markup (%)"
              type="number"
              value={settings.priceMarkup}
              onChange={(e) => setSettings({ ...settings, priceMarkup: parseInt(e.target.value) })}
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Minimum Lead Time (days)"
              type="number"
              value={settings.minLeadTime}
              onChange={(e) => setSettings({ ...settings, minLeadTime: parseInt(e.target.value) })}
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Maximum Advance Booking (days)"
              type="number"
              value={settings.maxAdvanceBooking}
              onChange={(e) => setSettings({ ...settings, maxAdvanceBooking: parseInt(e.target.value) })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupDialog(false)}>Cancel</Button>
          <Button onClick={handleSetupIntegration} variant="contained">
            Setup Integration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OTAManagement;